import videojs from 'video.js';
import { reportMediaView } from '../../api/player';

import ReportingPlayDetector from './reporting/reporting-play-detector.js'
import ReportingTimelineSegments from './reporting/reporting-timeline-segments.js'

const _segmentsMemory = new WeakMap();
const _playDetector = new WeakMap();
const _includeBookmark = new WeakMap();

const DETECTOR_TOLERANCE_FACTOR = 0.5;
const INACTIVITY_DELAY = 2 * 1000; // milliseconds
const HEARTBEAT_DELAY = 30 * 1000; // milliseconds
const SEGMENT_COMBINATION_TOLERANCE_SECONDS = 1;
const NO_BOOKMARK_ZONE_SIZE = 3; // seconds withing video duration in which we won't bookmark.

/**
 * The Reporting module.
 * @member {ReportingPlugin} reporting
 * @memberof MediasitePlayer#
 */

/**
 * Tracks the time ranges that have been played so far and periodically reports them back to the server.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
export default class ReportingPlugin extends videojs.getPlugin('plugin') {
    /**
     *
     * @param {videojs.Player} player
     * @param {Object} [options]
     */
    constructor(player, options) {
        super(player, options);

        /**
         * A playback ticket used to identify the current presentation when reporting playback statistics back to the server.
         * @type {string}
         * @memberof MediasitePlayer.reporting
         */
        this.playbackTicket = options?.playbackTicket ?? '';

        this.isLive = this.player.usingPlugin('live');
        this.serviceReportMediaViewCalledCount = 0;
        const segmentsMemory = new ReportingTimelineSegments(player);

        this.segmentsMemory = segmentsMemory

        this.playDetector = new ReportingPlayDetector(player);
        this.playDetector.tolerance = DETECTOR_TOLERANCE_FACTOR;
        this.playDetector.segmentStartAction = this.handleSegmentStart.bind(this);
        this.playDetector.segmentEndAction = this.handleSegmentEnd.bind(this);
    }

    /**
    * Whether or not the current playback time should be included in the reporting data. Setting this to <code>true</code> makes it possible to resume playback at the same position the next time the presentation is loaded.
    * @type {boolean}
    * @example
    * player.reporting.includeBookmark = true;
    */
    get includeBookmark() {
        return _includeBookmark.get(this) ?? true;
    }

    set includeBookmark(includeBookmark) {
        _includeBookmark.set(this, includeBookmark);
    }

    set segmentsMemory(segments) {
        _segmentsMemory.set(this, segments);
    }

    get segmentsMemory() {
        return _segmentsMemory.get(this);
    }

    set playDetector(playDetector) {
        _playDetector.set(this, playDetector);
    }

    get playDetector() {
        return _playDetector.get(this);
    }

    scheduleHeartbeat() {
        this.player.clearTimeout(this.heartbeatTimeout);
        this.heartbeatTimeout = this.player.setTimeout(this.onHeartbeat.bind(this), HEARTBEAT_DELAY);
    }

    onHeartbeat() {
        try {

            // Cause a segment end so we can report it.
            this.playDetector.endSegment();

            this.reportSegmentsSinceLastReport();

        } catch (err) {
            this.log('Error in Playback Reporting onHeartbeat');
        }

        // See you again soon!
        this.scheduleHeartbeat();
    }

    reportSegmentsSinceLastReport(synchronous) {
        var segments = this.segmentsMemory.getAllSegments();
        this.segmentsMemory.reset();

        this.reportPlayed(segments, synchronous);
    }

    handleSegmentStart(startPosition) {
        this.player.clearTimeout(this.inactivityTimeout);
    }

    handleSegmentEnd(startPosition, endPosition, isImportant) {
        this.segmentsMemory.addSegment(startPosition, endPosition);
        this.player.clearTimeout(this.inactivityTimeout);
    }

    onPlaybackRateChanged() {
        const newPlaybackRate = this.player.playbackRate();
        this.playDetector.handlePlaybackRateChange(newPlaybackRate);
    }

    onPositionChanged() {
        const position = this.player.currentTime();
        this.player.clearTimeout(this.inactivityTimeout);
        // ... but assume playback stopped if we don't hear more soon
        this.inactivityTimeout = this.player.setTimeout(function () {
            this.playDetector.endSegment();
        }.bind(this), INACTIVITY_DELAY);

        this.playDetector.handlePositionChange(position); // playDetector works in milliseconds.
    };

    trackPresentation() {
        this.isLive = this.player.usingPlugin('live');

        this.on(this.player, 'timeupdate', this.onPositionChanged);

        this.on(this.player, 'ratechange', this.onPlaybackRateChanged);


        // When the session ends, make sure to report end!
        window.addEventListener('beforeunload', e => {
            this.playDetector.endSegment();
            this.reportSegmentsSinceLastReport(true);
        });

        this.scheduleHeartbeat();
    };

    reportToDataProvider(playbackTicketId) {
        if (!playbackTicketId) {
            this.log("Reporting cannot report to data provider: no playback ticket or data provider");
            return;
        }
        this.playbackTicket = playbackTicketId;
    };

    reportPlayed(segments, synchronous) {
        let serviceSegments;
        let bookmarkPosition;

        if (!this.playbackTicket) {
            // Function was called before playbackTicketId and dataProvider were provided.
            return;
        }

        bookmarkPosition = this.calcBookmarkPosition(segments, this.isLive);
        serviceSegments = this.toServiceSegments(segments);

        if (serviceSegments.length < 1) {
            // There are no non-zero duration segments.
            return;
        }

        reportMediaView(this.playbackTicket, this.includeBookmark ? bookmarkPosition : 0, serviceSegments);

        if (this.includeBookmark) {
            const bookmarkStorage = this.player.bookmarkStorage();
            bookmarkStorage.save(bookmarkPosition);
        }

        this.serviceReportMediaViewCalledCount += 1;
        this.log('ReportMediaView ' + this.makeReportMediaViewDescription(serviceSegments, bookmarkPosition));
        this.log('ReportMediaView has been called ' + this.serviceReportMediaViewCalledCount + ' times.');
    }

    toServiceSegments(inputArray) {
        // Transforms an array of TimelineSegments into an array of segment 
        // objects appropriate for data provider's ReportMediaView method. 
        // 
        // Positions in the output are expressed in integers.
        // Abutting segments are combined in the output.


        let i, seg, start, end, duration;
        let resultArray = [];

        const getLastItem = function () {
            if (resultArray.length < 1) {
                return null;
            }

            return resultArray[resultArray.length - 1];
        };

        const getLastEnd = function () {
            const lastItem = getLastItem();

            if (!lastItem) {
                return NaN;
            }

            return lastItem.StartTime + lastItem.Duration;
        };

        const extendLastItem = function (start, duration) {
            let lastItem, newEnd, newDuration;

            newEnd = start + duration;
            lastItem = getLastItem();
            newDuration = newEnd - lastItem.StartTime;
            lastItem.Duration = newDuration;
        };

        const addNewItem = function (start, duration) {
            resultArray.push({ StartTime: start, Duration: duration });
        };

        const add = function (start, duration) {
            if (duration <= 0) {
                // Zero durations can be skipped.
                return;
            }

            if (Math.abs(start - getLastEnd()) <= SEGMENT_COMBINATION_TOLERANCE_SECONDS) {
                // This segment starts at or very near the end of the last one.
                extendLastItem(start, duration);
            } else {
                // This segment is not a continuation of the last.
                addNewItem(start, duration);
            }
        };

        for (i = 0; i < inputArray.length; i += 1) {
            seg = inputArray[i];
            start = Math.max(0, Math.floor(seg.start));
            end = Math.max(0, Math.floor(seg.end));
            duration = Math.max(0, end - start);

            add(start, duration);
        }

        return resultArray;
    };

    makeReportMediaViewDescription(serviceSegments, bookmarkPosition) {
        let i;
        const joint = ', ';
        let parts = [];

        function seviceSegmentDescription(serviceSegment) {
            const start = secondsToMinutesAndSeconds(serviceSegment.StartTime);
            const end = secondsToMinutesAndSeconds(serviceSegment.StartTime + serviceSegment.Duration);

            return '(' + start + ' - ' + end + ')';
        }

        function secondsToMinutesAndSeconds(totalSeconds) {
            let m, ss;
            const isNegative = totalSeconds < 0;

            totalSeconds = Math.floor(Math.abs(totalSeconds));
            m = Math.floor(totalSeconds / 60) + '';
            ss = (totalSeconds % 60) + '';
            while (ss.length < 2) {
                ss = '0' + ss;
            }
            if (isNegative) {
                m = '-' + m;
            }
            return m + ':' + ss;
        }

        for (i = 0; i < serviceSegments.length; i += 1) {
            parts.push(seviceSegmentDescription(serviceSegments[i]));
        }

        return 'segments: [' + parts.join(joint) + '], bookmarkPosition: ' + secondsToMinutesAndSeconds(bookmarkPosition);
    }

    calcBookmarkPosition(segments, isLive) {
        let result = 0;

        if (!isLive && segments.length > 0) {
            result = Math.floor(segments[segments.length - 1].end);

            if (this.isInNoBookmarkZone(result)) {
                // No sense bookmarking a position sooo close to 
                // the video duration. Interpret this to mean we 
                // reached the end and should clear out (zero) 
                // the bookmark.
                result = 0;
            }
        }

        return result;
    }

    isInNoBookmarkZone(position) {
        const diff = this.player.duration() - position;
        return diff >= 0 && diff <= NO_BOOKMARK_ZONE_SIZE;
    }
}

